import React, { useState } from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1536px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 40px;
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
  
  @media (max-width: 768px) {
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Tab = styled.button`
  padding: 15px 25px;
  background: ${props => props.active ? '#000' : '#f5f5f5'};
  color: ${props => props.active ? '#fff' : '#000'};
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  min-width: 120px;
  transition: background-color 0.3s, color 0.3s;
  
  &:hover {
    background: ${props => props.active ? '#000' : '#e0e0e0'};
  }
  
  @media (max-width: 768px) {
    min-width: auto;
    flex-shrink: 0;
  }
`;

const FAQContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid #e0e0e0;
  &:last-child {
    border-bottom: none;
  }
`;

const FAQQuestion = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background-color: #fff;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const QuestionText = styled.h3`
  font-size: 16px;
  font-weight: 500;
  margin: 0;
`;

const ToggleIcon = styled.span`
  font-size: 20px;
  font-weight: 300;
`;

const FAQAnswer = styled.div`
  padding: ${props => props.isOpen ? '20px' : '0 20px'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  line-height: 1.6;
  font-size: 14px;
  color: #333;
`;

const FAQPage = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [openItems, setOpenItems] = useState({ 0: true });
  
  const toggleFAQ = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  const faqData = {
    products: [
      {
        question: "What is Streetwear?",
        answer: "TheMadZone thinks streetwear transcends the ordinary. Born from the pulse of urban culture, our style is a raw fusion, echoing the rebellious spirit of skateboarding and the rhythm of hip-hop cultures. Feel the edge in our comfortable yet audaciously expressive attire, marrying bold graphics with athletic swagger."
      },
      {
        question: "What is drop shoulders?",
        answer: "Drop shoulders is a style where the shoulder seam of a garment falls below the natural shoulder line, creating a relaxed, oversized look. This design gives a casual, laid-back appearance and is popular in streetwear fashion."
      },
      {
        question: "Why are so many products out of stock?",
        answer: "Our products are produced in limited quantities to maintain exclusivity and reduce waste. When items sell out quickly, it's a reflection of their popularity. We restock periodically, so we recommend signing up for notifications to be alerted when your favorite items are available again."
      },
      {
        question: "I want a product but it is out of stock. What can I do?",
        answer: "You can use the 'Notify Me' button on the product page to receive an email when the item is back in stock. Alternatively, follow us on social media where we announce restocks, or contact our customer service team who may be able to provide information about upcoming availability."
      },
      {
        question: "I like the style, but what size should I buy?",
        answer: "We recommend checking our detailed size guide available on each product page. Our streetwear tends to have a relaxed fit, so if you prefer a tighter fit, consider sizing down. If you're between sizes, we generally recommend going up for a more authentic streetwear look."
      },
      {
        question: "My item doesn't fit me. What should I do?",
        answer: "If your item doesn't fit correctly, you can return it within 7 days of the delivery date, provided it's unworn with tags attached. Please visit our Return Policy page for detailed instructions. Remember to include your Order ID in the return package."
      },
      {
        question: "Why is a tee so expensive?",
        answer: "Our tees are crafted from premium materials, ethically produced in limited quantities, and feature original designs. The price reflects our commitment to quality, sustainability, fair labor practices, and the unique artistic value of each piece in our collection."
      }
    ],
    orders: [
      {
        question: "How do I track my order?",
        answer: "Once your order ships, you'll receive a tracking number via email. You can also check your order status by logging into your account and viewing your order history."
      },
      {
        question: "What is your shipping policy?",
        answer: "We currently ship only to the United Kingdom (England, Wales, Scotland, Northern Ireland, Isle of Man, and Isle of Skye). Orders £100.00 and above receive FREE shipping. Orders below £100.00 are charged a fixed £4.00 delivery fee (non-refundable). Standard delivery takes 3-5 business days. For more details, please visit our Shipping Policy page."
      },
      {
        question: "How much does shipping cost?",
        answer: "Shipping is FREE for orders £100.00 and above. Orders below £100.00 are charged a fixed £4.00 delivery fee. This delivery charge is non-refundable, even if you return items from your order."
      },
      {
        question: "How long does delivery take?",
        answer: "We aim to process and dispatch orders within 1-2 business days. Once dispatched, standard delivery takes 3-5 business days. Business days are Monday to Friday, excluding bank holidays."
      },
      {
        question: "Can I track my order?",
        answer: "Yes! Once your order ships, you'll receive a tracking number via email. You can use this to track your order's progress, or check your order status by logging into your account and visiting 'My Orders'."
      }
    ],
    profile: [
      {
        question: "How do I create an account?",
        answer: "Click on 'My Account' in the top navigation, then select 'Register'. Fill in your details to create your TheMadZone profile."
      },
      {
        question: "How do I update my personal information?",
        answer: "Log into your account, navigate to 'My Profile', and click 'Edit Profile' to update your information."
      }
    ],
    community: [
      {
        question: "How can I join the TheMadZone community?",
        answer: "Follow us on social media, sign up for our newsletter, and participate in our events. We also have a Discord server where our community connects."
      },
      {
        question: "Do you have any events or meetups?",
        answer: "Yes, we regularly host events in major cities. Check our Events page or follow us on social media for announcements."
      }
    ],
    miscellaneous: [
      {
        question: "What are MAD Coins?",
        answer: "MAD Coins are TheMadZone's reward currency. You earn coins based on your purchases, and each MAD Coin equals £1 in discount value when you use them at checkout. MAD Coins can be applied directly in your cart during checkout to reduce your order total."
      },
      {
        question: "How do I earn MAD Coins?",
        answer: "You earn MAD Coins automatically when you make purchases. The earning rate is determined by our rewards program settings. Coins are awarded after your order payment is successful and will appear in your account balance."
      },
      {
        question: "How do I use MAD Coins?",
        answer: "You can apply MAD Coins during checkout in your cart. Each MAD Coin gives you £1 discount. You can use as many MAD Coins as you have available, up to the total order value."
      },
      {
        question: "Do MAD Coins expire?",
        answer: "No, MAD Coins do not expire. You can accumulate and use them whenever you want."
      },
      {
        question: "How do I check my MAD Coins balance?",
        answer: "You can check your MAD Coins balance in the 'MY ACCOUNT' dropdown menu in the navigation bar, or visit the 'MAD Coin Activity' page to see your balance and transaction history."
      },
      {
        question: "What is the referral program?",
        answer: "When you refer a friend and they sign up using your referral link, you'll earn 10 MAD Coins once they spend £100 or more on orders. That's £10 worth of rewards! Share your unique referral link from the 'Refer & Earn' page to start earning."
      },
      {
        question: "Do you offer gift cards?",
        answer: "Yes, digital gift cards are available for purchase on our website in various denominations. However, please note that gift cards are non-refundable once the transaction is successful. All gift card sales are final."
      },
      {
        question: "How can I contact customer support?",
        answer: "You can reach our customer support team via email at hello@themadzone.com or by visiting our Support & Help page where you can submit a support request form. We typically respond within 24-48 hours during business days."
      },
      {
        question: "What is your return policy?",
        answer: "You have 7 days from the delivery date to return items. Items must be unworn, unwashed, and in original condition with tags attached. You must include your Order ID in the return package. Return shipping costs are the customer's responsibility. For full details, please visit our Return Policy page."
      }
    ]
  };
  
  return (
    <PageContainer>
      <SEO 
        title="Frequently Asked Questions"
        description="Find answers to common questions about TheMadZone products, orders, shipping, returns, MAD Coins, referrals, and more. Get help with your account, payments, and delivery."
        keywords="FAQ, help, support, questions, TheMadZone, streetwear, orders, shipping, returns, MAD Coins"
      />
      <NavbarSpacer />
      <ContentWrapper>
        <Title>FAQs</Title>
        
        <TabsContainer>
          <Tab 
            active={activeTab === 'products'} 
            onClick={() => setActiveTab('products')}
          >
            Products
          </Tab>
          <Tab 
            active={activeTab === 'orders'} 
            onClick={() => setActiveTab('orders')}
          >
            Orders
          </Tab>
          <Tab 
            active={activeTab === 'profile'} 
            onClick={() => setActiveTab('profile')}
          >
            My Profile
          </Tab>
          <Tab 
            active={activeTab === 'community'} 
            onClick={() => setActiveTab('community')}
          >
            Community
          </Tab>
          <Tab 
            active={activeTab === 'miscellaneous'} 
            onClick={() => setActiveTab('miscellaneous')}
          >
            Miscellaneous
          </Tab>
        </TabsContainer>
        
        <FAQContainer>
          {faqData[activeTab].map((faq, index) => (
            <FAQItem key={index}>
              <FAQQuestion onClick={() => toggleFAQ(index)}>
                <QuestionText>{faq.question}</QuestionText>
                <ToggleIcon>{openItems[index] ? '−' : '+'}</ToggleIcon>
              </FAQQuestion>
              <FAQAnswer isOpen={openItems[index]}>
                {faq.answer}
              </FAQAnswer>
            </FAQItem>
          ))}
        </FAQContainer>
      </ContentWrapper>
    </PageContainer>
  );
};

export default FAQPage; 